<template>
    <!--Page alert + loader-->
    <PageStatus :alert="defaultAlert" :table-data="tableData" />

    <!--Import Data-->
    <ImportModal
        :form-import-data="formImportData"
        :alert="defaultAlert"
        ask-source-system
        v-model:source-system="formImportData.source_system"
        @file-change="onFileImportChange"
        @import="importRecords"
        @download-template="downloadImportTemplate"
    >
                <!--Import Notes-->
        <div id="accordion">
            <div class="card">
                <div class="card-header">
                    <a class="btn" data-bs-toggle="collapse" href="#collapseOne">
                        Import Process
                    </a>
                </div>
                <div id="collapseOne" class="collapse show" data-bs-parent="#accordion">
                    <div class="card-body">
                        <ol>
                            <li>Click <b>Download Import Template</b>. It has the right columns and two sample rows. Columns marked <b>*</b> (red) are required.</li>
                            <li>Delete the sample rows and paste your data, one record per row. Keep row 1 (the column names). The order of the columns does not matter.</li>
                            <li><b>Old system ID</b>: the record's ID in your old system. Records whose Old system ID was already imported are skipped.</li>
                            <li><b>Old system branch ID</b>: the old ID of a branch you imported earlier. Leave it blank to use your current branch (or the Head Office when you are on All Branches).</li>
                            <li>Enter the <b>Old system name</b> (e.g. quickbooks). Use the same name for every file from that system.</li>
                            <li>Records are always imported into the company you are logged into. For big imports with undo, use <b>Data Migration</b>.</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
        <!--End Import Notes-->

    </ImportModal>
    <!--Import Data-->

    
    <!--Filter Data Modal (shared: FilterModal.vue)-->
    <FilterModal
        :draft="filterDraft"
        v-model:search-by="tableData.searchBy"
        :view-options="viewOptions"
        :search-by-options="searchByOptions"
        :alert="defaultAlert"
        @search-by-change="changePlaceholder()"
        @apply="applyFilters"
        @clear="clearFilters"
    >
        <!-- Status: one or more -->
        <SelectField
            v-model="filterDraft.statuses"
            col="mb-3"
            label="Status"
            icon="fa fa-flag"
            class="multiSelect-validation-field"
            :options="statusOptions"
            :reduce="option => option.value"
            :close-on-select="false"
            placeholder="All statuses"
            multiple
        />

        <!-- Tags: records with ANY of the chosen tags -->
        <TagField
            v-model="filterDraft.tagIds"
            col="mb-3"
            label="Tags"
            icon="fa fa-tags"
            :tags="tagOptions"
            :allow-create="false"
            placeholder="All tags"
            hint="Shows records with any of the chosen tags"
        />
    </FilterModal>
    <!--End Filter Data Modal-->

<!--Begin form-->
<RecordFormModal
    :title="isEditing ? 'Update Personal Information' : 'Add Personal Information'"
    title-icon="bi bi-person"
    :is-editing="isEditing"
    :is-saving="isSaving"
    :alert="defaultAlert"
    @save="saveOrEditRecord"
    @sample="addSampleData"
    @reset="resetForm"
>
    <!--Below id data id. User id is accessed directly in computed properties inside formData_fields function-->
    <HiddenField v-model="formInputs.id" />

    <!-- Row 1: Names -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.firstname"
            col="col-md-6"
            label="First Name"
            icon="fa fa-user"
            placeholder="Enter name"
            tooltip="Please enter your first name"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.lastname"
            col="col-md-6"
            label="Last Name"
            icon="fa fa-user"
            placeholder="Enter Last Name"
            tooltip="Please enter your last name"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Row 2: Email & Phone -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.email"
            col="col-md-6"
            type="email"
            label="Email"
            icon="fa fa-envelope"
            placeholder="Enter email"
            tooltip="Please enter a valid email"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.phonenumber"
            col="col-md-6"
            type="tel"
            label="Phone"
            icon="fa fa-phone"
            placeholder="Enter number"
            tooltip="Enter your phone or ID number"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Row 3: Website & Sex -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.website"
            col="col-md-6"
            type="url"
            label="Website"
            icon="fa fa-globe"
            placeholder="https://example.com"
            tooltip="Enter your website URL"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <SelectField
            v-model="formInputs.sex"
            col="col-md-6"
            name="sex"
            label="Sex"
            icon="fa fa-venus-mars"
            :options="sexOptions"
            :reduce="option => option.value"
            placeholder="Select sex"
            tooltip="Select your sex"
            :submitted="submitted"
            required
            @change="$clearValidationAlert('#recordForm')"
        />
    </div>

    <!-- Row 3b: Status -->
    <div class="row g-3 mb-0">
        <SelectField
            v-model="formInputs.status"
            col="col-12"
            name="status"
            label="Status"
            icon="fa fa-flag"
            :options="statusOptions"
            :reduce="option => option.value"
            placeholder="Select status"
            tooltip="Please select the status"
            :submitted="submitted"
            required
            @change="$clearValidationAlert('#recordForm')"
        />
    </div>

    <!-- Row 3c: Tags -->
    <div class="row g-3 mb-0">
        <TagField
            v-model="formInputs.tags"
            col="col-12"
            label="Tags"
            icon="fa fa-tags"
            :tags="tagOptions"
            :api="tagsEndpoint"
            :allow-create="$hasPermission('tags.add')"
            :hint="$hasPermission('tags.add') ? `Type to search. If the tag doesn't exist, choose “Add tag”.` : 'Type to search the tags.'"
            @created="tag => tagOptions.push(tag)"
        />
    </div>

    <div class="card-header bg-sa-card-header d-flex justify-content-between align-items-center">
        <span><i class="bi bi-ui-checks me-2"></i>Options</span>
    </div>

        <!-- Row 4: Fancy Checkboxes & Radios -->
    <div class="row g-3 mb-0">
        <CheckField
            v-model="formInputs.checkbox1"
            col="col-md-6"
            label="Fancy Checkboxes"
            layout="horizontal"
            text="Check me"
            tooltip="Please tick Check me"
            required
            @change="handleInputChange"
        >
            <CheckField v-model="formInputs.checkbox2" text="Another check" />
        </CheckField>
        <RadioField
            v-model="formInputs.radio1"
            col="col-md-6"
            label="Fancy Radio Buttons"
            name="radioGroup"
            :options="sexOptions"
            tooltip="Please choose Male or Female"
            align="end"
            required
            @change="handleInputChange"
        />
    </div>

        <!-- Row 5: File Upload & Date -->
    <div class="row g-3 mb-3">
        <FormField
            v-model="formInputs.file"
            col="col-md-6"
            type="file"
            label="File Upload"
            icon="fa fa-image"
            tooltip="Please choose an image"
            :required="!isEditing"
            @change="handleInputChange"
        />
        <DateField
            v-model="formInputs.date"
            col="col-md-6"
            label="Date"
            icon="fa fa-calendar"
            tooltip="Please choose a date"
            required
            @input="handleInputChange"
            @change="handleInputChange"
            with-time
        />
    </div>

    <div class="card-header bg-sa-card-header d-flex justify-content-between align-items-center">
        <span><i class="bi bi-cash-coin me-2"></i>More Fields</span>
    </div>

    <!-- Row 5b: Money & Referred By -->
    <div class="row g-3 mb-0">
        <MoneyField
            v-model="formInputs.amount"
            col="col-md-6"
            label="Amount"
            icon="fa fa-money"
            min="0"
            tooltip="Enter the amount, e.g. 1,250.00"
            required
            @input="handleInputChange"
        />
        <SearchSelectField
            v-model="formInputs.referred_by_id"
            col="col-md-6"
            name="referred_by_id"
            label="Referred By"
            icon="fa fa-user-plus"
            :api="apiUrl"
            :selected-text="formInputs.referred_by_name"
            tooltip="Type a first name to find another record"
        />
    </div>

    <!-- Row 5c: Access code + confirm (stored hashed; empty when editing = keep the current one) -->
    <div class="row g-3 mb-0">
        <PasswordField
            v-model="formInputs.access_code"
            col="col-md-6"
            name="access_code"
            label="Access Code"
            icon="fa fa-lock"
            minlength="4"
            :tooltip="isEditing ? 'Leave empty to keep the current code' : 'At least 4 characters'"
            @input="handleInputChange"
        />
        <PasswordField
            v-model="formInputs.access_code_confirm"
            col="col-md-6"
            label="Confirm Access Code"
            icon="fa fa-lock"
            data-match="access_code"
            tooltip="Must be the same as the access code"
            @input="handleInputChange"
        />
    </div>

    <!-- Row 5d: Notes -->
    <div class="row g-3 mb-3">
        <TextareaField
            v-model="formInputs.notes"
            col="col-12"
            label="Notes"
            icon="fa fa-sticky-note"
            rows="3"
            maxlength="500"
            tooltip="Anything we should know (up to 500 characters)"
            @input="handleInputChange"
        />
    </div>

    <div class="card-header bg-sa-card-header d-flex justify-content-between align-items-center">
        <span><i class="bi bi-list-ul me-2"></i>The Dynamic Fields</span>
    </div>

    <!-- Row 6: Dynamic Fields -->
    <div class="row g-3 mb-3">
        <div class="col-12">
            <div id="dynamicFieldsContainer">
                <!-- Same sizes on every screen (side by side on phones too) -->
                <div class="row g-0 dynamic-header fw-bold flex-nowrap">
                    <div class="col-auto dyn-narrow"><div class="p-2">#</div></div>
                    <div class="col"><div class="p-2">Name</div></div>
                    <div class="col-4"><div class="p-2">Age</div></div>
                    <div class="col-auto dyn-narrow"><div class="p-2 text-center"></div></div>
                </div>
                <div
                    v-for="(field, index) in dynamicFields.fields"
                    :key="index"
                    class="row g-0 align-items-center dynamic-row flex-nowrap"
                >
                    <div class="col-auto dyn-narrow">
                        <span class="input-group-text w-100 justify-content-center"> {{index + 1}} </span>
                    </div>
                    <HiddenField v-model="field.id" />
                    <FormField
                        v-model="field.fname"
                        col="col"
                        compact
                        :placeholder="'Enter Name ' + (index + 1)"
                        :tooltip="'Please enter name ' + (index + 1)"
                        required
                        @input="handleInputChange"
                        @change="handleInputChange"
                    />
                    <FormField
                        v-model="field.fage"
                        col="col-4"
                        compact
                        :placeholder="'Enter Age ' + (index + 1)"
                        :tooltip="'Please enter age ' + (index + 1)"
                        required
                        @input="handleInputChange"
                        @change="handleInputChange"
                    />
                    <div class="col-auto dyn-narrow">
                        <button
                            @click="removeField(index, field.id)"
                            type="button"
                            class="btn btn-danger btn-sm remove-row w-100"
                        >
                            <i class="fa fa-trash"></i>
                        </button>
                    </div>
                </div>
            </div>
            <button @click="addField" type="button" class="btn btn-outline-primary btn-sm mt-2">
                <i class="fa fa-plus"></i> Add Row
            </button>
        </div>
    </div>
</RecordFormModal>
<!--End form-->


       <h4 class="sa-core-color fw-semibold mb-2"><i class="fa fa-flask me-2 opacity-75"></i>{{ pageTitle }}</h4>
    

<!--Tables-->
<div class="card glass mt-0 bg-sa-card">
    <div class="card-body bg-sa-card-body p-0">

        <!-- Toggleable Tabs (one tab per status, same page) -->
        <ul class="nav nav-tabs app-tabs" role="tablist">
            <li class="nav-item" role="presentation">
                <a
                    href="#"
                    class="nav-link"
                    :class="{ 'app-tab-active': activeTab === 'all' }"
                    @click.prevent="selectStatusTab('all')"
                >
                    <span class="sa-core-color"><i v-if="$showStatusIcons" class="fa fa-list me-1"></i>All</span>
                </a>
            </li>
            <li v-for="option in statusOptions" :key="option.value" class="nav-item" role="presentation">
                <a
                    href="#"
                    class="nav-link"
                    :class="{ 'app-tab-active': activeTab === option.value }"
                    @click.prevent="selectStatusTab(option.value)"
                >
                    <span class="sa-core-color"><i v-if="$showStatusIcons && option.icon" :class="[option.icon, 'me-1']"></i>{{ option.label }}</span>
                </a>
            </li>
        </ul>
        <!-- End Toggleable Tabs -->

        <!-- Table action row (shared: TableToolbar.vue) -->
        <TableToolbar
            :can-add="$hasPermission('crud_online.add')"
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            :can-delete="$hasPermission('crud_online.delete')"
            @add="clearForm()"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
        >
            <!-- More menu -->
            <template #more>
                        <li><h6 class="dropdown-header">Data</h6></li>
                        <li>
                            <button type="button" class="dropdown-item" data-bs-toggle="modal" data-bs-target="#importModal">
                                <i class="fa fa-upload fa-fw me-1"></i> Import
                            </button>
                        </li>
                        <li>
                            <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                                <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                            </button>
                        </li>
                        <li>
                            <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                                <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                            </button>
                        </li>
                        <li>
                            <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                                <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                            </button>
                        </li>

                        <template v-if="$hasPermission('tags.view')">
                            <li><hr class="dropdown-divider"></li>
                            <li><h6 class="dropdown-header">Settings</h6></li>
                            <li>
                                <router-link class="dropdown-item" :to="{ path: '/settings/tags/', query: { module: 'crud_online' } }">
                                    <i class="fa fa-tags fa-fw me-1"></i> Manage tags
                                </router-link>
                            </li>
                        </template>
            </template>
        </TableToolbar>
        <!-- Table Search Panel -->
        <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
        />
        <!-- What the table is currently showing -->
        <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
        />
        <!-- Table -->
        <div class="table-responsive">
            <table 
                class="table table-hover align-middle mb-0" 
                id="bootstrapTable"
                tabindex="0"
                @keydown.up="$moveRowSelection(-1, $event)"
                @keydown.down="$moveRowSelection(1, $event)"
                @keydown.enter="$onTableEnter($event)">
                <thead>
                    <tr>
                        <th>
                            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
                        </th>
                        <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
                        <th class="button hide-sm-td">No</th>
                        <SortableTh v-if="isColumnVisible('firstname')" column="firstname" :table-data="tableData" @sort="orderBy">First Name</SortableTh>
                        <SortableTh v-if="isColumnVisible('email')" column="email" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Email</SortableTh>
                        <SortableTh v-if="isColumnVisible('phonenumber')" column="phonenumber" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Phone</SortableTh>
                        <SortableTh v-if="$hasPermission('view-location') && isColumnVisible('current_location')" column="current_location" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Current Location</SortableTh>
                        <SortableTh v-if="isColumnVisible('status')" column="status" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Status</SortableTh>
                        <th v-if="isColumnVisible('tags')" class="button hide-sm-td">Tags</th>
                        <th v-if="isColumnVisible('file')" class="button hide-sm-td">File</th>
                        <SortableTh v-if="isColumnVisible('date_created')" column="date_created" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Date</SortableTh>
                        <th class="button">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(record, index) in tableData.records" :key="record.id">
                        <tr class="clickable-row"
                            :data-record-id="record.id"
                            :class="{ 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) }"
                            @click="$onRowClick(record, $event)"
                            @dblclick="$onRowDblClick(record, $event)"
                        >
                            <td>
                                <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
                            </td>
                            <td class="expand-cell hide-sm-td"
                                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                                @click="$toggleDetails(record.id)">
                                <i
                                class="fa fa-lg expand-icon ms-1"
                                :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle'"
                            ></i>
                            </td>
                            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
                            <td v-if="isColumnVisible('firstname')">
                            <!-- Phones: + / − and the number go in front of the name (their columns are hidden) -->
                            <span
                                class="show-sm-inline expand-cell me-2"
                                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                                @click="$toggleDetails(record.id)"
                            >
                                <i
                                    class="fa fa-lg expand-icon"
                                    :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle'"
                                ></i>
                            </span>
                            <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>{{ record.firstname }}
                            </td>
                            <td v-if="isColumnVisible('email')" class="hide-sm-td">
                                <span
                                    v-if="record.email"
                                    class="cell-short cell-link"
                                    :title="record.email"
                                >{{ record.email }}</span>
                            </td>
                            <td v-if="isColumnVisible('phonenumber')" class="hide-sm-td">{{ record.phonenumber }}</td>
                            <td v-if="$hasPermission('view-location') && isColumnVisible('current_location')" class="hide-sm-td">{{ record.current_location }}</td>
                            <td v-if="isColumnVisible('status')" class="hide-sm-td">
                                <StatusBadge :status="record.status" :options="statusOptions" />
                            </td>
                            <td v-if="isColumnVisible('tags')" class="hide-sm-td text-nowrap">
                                <TagBadges :tags="record.tags" :max="2" />
                            </td>
                            <td v-if="isColumnVisible('file')" class="hide-sm-td">
                                <img v-if="record.file_path" :src="`${$baseUrl}/crud/${record.file_path}`" height="30" />
                            </td>
                            <td v-if="isColumnVisible('date_created')" class="hide-sm-td text-nowrap" :title="$ConvertToLocalDate(record.date_created)">{{ $friendlyDate(record.date_created) }}</td>
                            <td>
                                <!--Delete Modal Part-->
                                <RowActions
                                    :record="record"
                                    :modal-id="'deleteCrudOnlineModal' + record.id"
                                    :can-edit="$hasPermission('crud_online.delete')"
                                    :can-soft-delete="$hasPermission('crud_online.delete')"
                                    :can-restore="$hasPermission('crud_online.delete')"
                                    :can-delete-permanently="$hasPermission('crud_online.delete')"
                                    @edit="editRecord"
                                    @delete-type="setDeleteType"
                                />
                                <DeleteModal
                                    :modal-id="'deleteCrudOnlineModal' + record.id"
                                    :title="deleteModalTitle"
                                    :message="deleteModalMessage"
                                    @confirm="deleteRecord(record.id)"
                                />
                                <!--End Delete Part Modal-->
                            </td>
                        </tr>
                        <!-- Hidden expanded row -->
                        <tr class="collapse-row">
                            <td class="p-0"></td>
                            <td class="p-0 hide-sm-td"></td>
                            <td colspan="9" class="p-0">
                                <div :id="'tableDetails'+ record.id" class="collapse table-collapse table-details">
                                    
                                    <div class="p-3 details-list">
    <div><i class="fa fa-hashtag"></i><strong>Crud Number:</strong> {{ record.crud_number }}</div>
    <div><i class="fa fa-user"></i><strong>Creator Name:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
    <div><i class="fa fa-building"></i><strong>Creator Company Name:</strong> {{ record.company_name }}</div>
    <div><i class="fa fa-sitemap"></i><strong>Creator Branch Name:</strong> {{ record.branch_name }}</div>

    <!-- Shown on phones, or always when the column is hidden with the Columns button -->
    <div :class="{ 'show-sm-td': isColumnVisible('firstname') }"><i class="fa fa-user"></i><strong>First Name:</strong> {{ record.firstname }}</div>
    <div><i class="fa fa-user"></i><strong>Last Name:</strong> {{ record.lastname }}</div>
    <div :class="{ 'show-sm-td': isColumnVisible('email') }"><i class="fa fa-envelope"></i><strong>Email:</strong> <a v-if="record.email" :href="`mailto:${record.email}`">{{ record.email }}</a></div>
    <div :class="{ 'show-sm-td': isColumnVisible('phonenumber') }"><i class="fa fa-phone"></i><strong>Phone Number:</strong> {{ record.phonenumber }}</div>
    <div v-if="$hasPermission('view-location')" :class="{ 'show-sm-td': isColumnVisible('current_location') }"><i class="fa fa-map-marker"></i><strong>Current Location:</strong> {{ record.current_location }}</div>
    <div :class="{ 'show-sm-td': isColumnVisible('status') }"><i class="fa fa-flag"></i><strong>Status:</strong> <StatusBadge :status="record.status" :options="statusOptions" /></div>
    <div :class="{ 'show-sm-td': isColumnVisible('tags') }"><i class="fa fa-tags"></i><strong>Tags:</strong> <TagBadges :tags="record.tags" /></div>

    <div>
        <i class="fa fa-globe"></i><strong>Website:</strong>
        <a
            v-if="record.website"
            :href="/^https?:\/\//i.test(record.website) ? record.website : `https://${record.website}`"
            target="_blank"
            rel="noopener noreferrer"
        >{{ record.website }} <i class="fa fa-external-link small"></i></a>
    </div>
    <div><i class="fa fa-venus-mars"></i><strong>Sex:</strong> {{ record.sex }}</div>
    <div><i class="fa fa-money"></i><strong>Amount:</strong> {{ record.amount !== null && record.amount !== undefined && record.amount !== '' ? $formatMoney(record.amount) : '' }}</div>
    <div><i class="fa fa-user-plus"></i><strong>Referred By:</strong> {{ record.referred_by_name }}</div>
    <div><i class="fa fa-sticky-note"></i><strong>Notes:</strong> <span class="text-pre-line">{{ record.notes }}</span></div>

    <div :class="{ 'show-sm-td': isColumnVisible('file') }">
        <i class="fa fa-image"></i><strong>Image:</strong>
        <img v-if="record.file_path" :src="`${$baseUrl}/crud/${record.file_path}`" height="30" />
    </div>

    <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
    <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ $ConvertToLocalDate(record.date_updated) }}</div>
    <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
</div>

                                </div>
                            </td>
                        </tr>
                    </template>
                    <!-- No rows: blank while loading, then the empty message in the middle -->
                    <tr v-if="tableData.records.length === 0" class="table-filler">
                        <td colspan="12" class="text-center align-middle" :style="{ height: fullPageHeight() }">
                            <TableEmptyState
                                v-if="!tableLoading"
                                :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
                                :can-add="$hasPermission('crud_online.add')"
                                @add="clearForm()"
                                @clear="clearAllFilterChips"
                            />
                        </td>
                    </tr>
                </tbody>
                <tfoot class="table-light">
                    <tr>
                        <td colspan="12">
                            <!--Pagination (hidden when there are no rows; its space is kept)-->
                            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
                            <Pagination
                                v-else
                                :currentPage="tableData.currentPage"
                                :totalPages="tableData.totalPages"
                                :hasNextPage="tableData.hasNextPage"
                                :totalRecords="tableData.totalRecords"
                                :startRecord="tableData.startRecord"
                                :endRecord="tableData.endRecord"
                                :showFullPagination="tableData.totalRecords > 0"
                                :lastKnownPage="lastKnownPage"
                                @page-changed="fetchRecords"
                                @loaderMessage="updateLoaderMessage"
                                @last-page-request="goToLastPage"
                            />
                            <!--End Pagination-->
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
        <!-- Bottom Controls: Delete & Pagination -->
    </div>
</div>
<!--End Tables-->

    
</template>
<script>
    /*
     * CRUD Online Only: template for pages that save online only (most pages).
     * Viewing works offline like every table page (tableMixin), but saving needs internet.
     * For a page that also saves offline, copy CrudOfflineOnline.vue instead.
     */
    import "vue-select/dist/vue-select.css";

    import PageStatus from "../../../components/admin/PageStatus.vue";
    import TableToolbar from "../../../components/admin/TableToolbar.vue";
    import TagBadges from "../../../components/admin/TagBadges.vue";
    import Pagination from "../../../components/admin/Pagination.vue";
    import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
    import SortableTh from "../../../components/admin/SortableTh.vue";
    import DeleteModal from "../../../components/admin/DeleteModal.vue";
    import RowActions from "../../../components/admin/RowActions.vue";
    import SearchBox from "../../../components/admin/SearchBox.vue";
    import ImportModal from "../../../components/admin/ImportModal.vue";
    import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
    import FormField from "../../../components/admin/FormField.vue";
    import SelectField from "../../../components/admin/SelectField.vue";
    import FilterModal from "../../../components/admin/FilterModal.vue";
    import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
    import StatusBadge from "../../../components/admin/StatusBadge.vue";
    import TagField from "../../../components/admin/TagField.vue";
    import HiddenField from "../../../components/admin/HiddenField.vue";
    import CheckField from "../../../components/admin/CheckField.vue";
    import RadioField from "../../../components/admin/RadioField.vue";
    import TextareaField from "../../../components/admin/TextareaField.vue";
    import MoneyField from "../../../components/admin/MoneyField.vue";
    import PasswordField from "../../../components/admin/PasswordField.vue";
    import SearchSelectField from "../../../components/admin/SearchSelectField.vue";
    import DateField from "../../../components/admin/DateField.vue";
    import crudOnlineOnlyMixin from "./mixins/crudOnlineOnlyMixin.js";
    import tableMixin from "../../../mixins/tableMixin.js";
    import axios from "axios";

    export default {
        name: "CrudOnlineOnly",
        mixins: [tableMixin, crudOnlineOnlyMixin], // Inject all methods here
        components: {
            PageStatus,
            TableToolbar,
            TagBadges,
            Pagination,
            TableEmptyState,
            SortableTh,
            DeleteModal,
            RowActions,
            SearchBox,
            ImportModal,
            RecordFormModal,
            FormField,
            SelectField,
            FilterModal,
            ActiveFilters,
            StatusBadge,
            TagField,
            HiddenField,
            CheckField,
            RadioField,
            TextareaField,
            MoneyField,
            PasswordField,
            SearchSelectField,
            DateField,
        },
        data() {
            return {
                // Standard data (isSaving, tableData, defaultAlert, formImportData ...) comes from tableMixin
                standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
                formInputs: this.getDefaultFormInputs(),
                apiUrl: "/crud/api_crud_online.php",     // used by tableMixin's fetchRecords, delete, import, export
                importTemplateFileName: "crud_online_import_template.xlsx",
                exportFileName: "crudOnlineExport", // name of the exported file
                columnsKey: "crud_online",           // saves this page's hidden columns (Columns button)
                extraFilterKeys: ["statuses", "tagIds"], // extra filters in the Filter window
                usesTags: true,                   // tag list + "Add tag" go to apiUrl (action=getTags / createTag)
                dynamicFields: this.getDefaultDynamicField(),
                sexOptions: [
                    { label: "Male", value: "male" },
                    { label: "Female", value: "female" },
                ],
                // Filter window: View
                viewOptions: [
                    { label: "All Records", value: "all" },
                    { label: "Records By Me", value: "byme" },
                    { label: "Records Deleted", value: "deletedone" },
                ],
                // Filter window: Search By
                searchByOptions: [
                    { label: "Firstname", value: "firstname" },
                    { label: "Lastname", value: "lastname" },
                    { label: "Email", value: "email" },
                    { label: "Phone Number", value: "phonenumber" },
                    { label: "Website", value: "website" },
                    { label: "Sex", value: "sex" },
                    { label: "Crud Number", value: "crud_number" },
                ],
                // Statuses (same list as crudStatuses() in api_crud_online_helpers.php)
                statusOptions: [
                    { label: "Draft", value: "draft", color: "secondary", icon: "fa fa-pencil" },
                    { label: "Pending", value: "pending", color: "warning", icon: "fa fa-clock-o" },
                    { label: "Approved", value: "approved", color: "success", icon: "fa fa-check-circle" },
                    { label: "Cancelled", value: "cancelled", color: "danger", icon: "fa fa-times-circle" },
                ],
            };
        },
        async created() {
            await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
            this.$checkIfCanFetchLocation(); //Only applies if location is enabled for user. If not Log user out for him to enable location
        },
        computed: {
            // Which status tab is highlighted, based on the current status filter
            activeTab() {
                const statuses = this.tableData.statuses || [];
                if (statuses.length === 0) return "all";
                if (statuses.length === 1) return statuses[0];
                return null; // several statuses chosen in the Filter window: no tab highlighted
            },
            // Page heading that follows the chosen filter.
            pageTitle() {
                const titles = {
                    all: "CRUD Online Only. DB Name is: crud_online_table",
                    byme: "CRUD Online Only By Me",
                    deletedone: "CRUD Online Only Deleted Records",
                };
                return titles[this.tableData.recordFilter] || "CRUD Online Only";
            },
            // Columns the user can show or hide (Columns button)
            tableColumns() {
                return [
                    { key: "firstname", label: "First Name" },
                    { key: "email", label: "Email" },
                    { key: "phonenumber", label: "Phone" },
                    { key: "status", label: "Status" },
                    { key: "tags", label: "Tags" },
                    ...(this.$hasPermission("view-location") ? [{ key: "current_location", label: "Current Location" }] : []),
                    { key: "file", label: "File" },
                    { key: "date_created", label: "Date" },
                ];
            },
        },
        methods: {
            // Status tabs: set the status filter and reload from page 1
            selectStatusTab(status) {
                this.tableData.statuses = status === "all" ? [] : [status];
                this.tableData.currentPage = 1;
                this.fetchRecords(
                    1,
                    this.tableData.currentSortColumn,
                    "DESC",
                    this.tableData.startDate,
                    this.tableData.endDate,
                    this.$loaderMessages.loaderMessageDefault,
                );
            },
            getDefaultFormInputs() {
                return {
                    //Form Data
                    id: "",
                    firstname: "",
                    lastname: "",
                    name: "",
                    email: "",
                    phonenumber: "",
                    website: "",
                    sex: "",
                    status: "",
                    tags: [],             // tag ids
                    checkbox1: "-",
                    checkbox2: "-",
                    radio1: false,
                    radio2: false,
                    crudImage: "",
                    date: "",
                    select_drop_down: [],
                    file: null,
                    notes: "",                 // TextareaField
                    amount: null,              // MoneyField (a number, or null)
                    access_code: "",           // PasswordField (never filled when editing)
                    access_code_confirm: "",
                    referred_by_id: null,      // SearchSelectField
                    referred_by_name: "",      // its name, shown when editing
                };
            },
            getDefaultFormImportData() {
                return {
                    dataFile: null,
                    source_system: "",      // old system name, e.g. quickbooks
                    templateLoading: false, // spinner on the Download Import Template button
                    importErrors: [],       // rows that were not imported, and why
                    uploading: false,
                    progress: 0,
                    message: "",
                    messageType: "",
                };
            },
            // Table defaults: the standard ones (tableMixin) + this page's own
            getDefaultTableData() {
                return {
                    ...this.baseTableData(),
                    searchBy: "firstname",
                    searchPlaceHolderText: "Search by firstname",
                    statuses: [],   // status filter (empty = all)
                    tagIds: [],     // tag filter (empty = all)
                };
            },
            getDefaultDynamicField() {
                return {
                    fields: [
                        {
                            id: "new",
                            fname: "",
                            fage: "",
                        },
                    ],
                };
            },            
            saveOrEditRecord() {
                // Ignore the call if a save is already running.
                if (this.isSaving) return;
                this.isSaving = true;

                try {
                    this.$scrollToTopModal("recordFormModal");
                    this.submitted = true;
                    const formIsValid = this.$validateForm("#recordForm");
                    if (!formIsValid) {
                        this.$playErrorSound();
                        this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
                        this.isSaving = false;   // CHANGED: nothing was sent, allow another try
                        return;
                    }
                    this.handleAlert("starting", "Please wait submiting", "info", "modal");
                    let formData = this.formData_fields();
                    // New records should appear on the first page.
                    // Editing existing records should stay on the current page.
                    if (!this.formInputs.id) {
                        this.tableData.currentPage = 1;
                    }

                    // Add dynamic fields (fname and fage) to FormData fields
                    for (let i = 0; i < this.dynamicFields.fields.length; i++) {
                        const id = this.dynamicFields.fields[i].id === undefined ? "new" : this.dynamicFields.fields[i].id; // Checking if it was already inserted. If not set it to new
                        formData.append(`fields[${i}][id]`, id);
                        formData.append(`fields[${i}][fname]`, this.dynamicFields.fields[i].fname);
                        formData.append(`fields[${i}][fage]`, this.dynamicFields.fields[i].fage);
                    }
                    axios
                        .post(this.apiUrl, formData, this.$handleConfig())
                        .then((response) => {
                            if (response.data.status == "success") {
                                this.handleSaveOrEditSuccess(response);
                            } else {
                                this.handleError("starting", response.data.message[0], "danger", "modal");
                            }
                        })
                        .catch((error) => {
                            this.handleRequestError(error, "modal");
                        })
                        .finally(() => {
                            // CHANGED: allow saving again only once the server has answered.
                            this.isSaving = false;
                        });
                } catch (error) {
                    // CHANGED: if something crashes before the request is sent, don't stay locked.
                    this.isSaving = false;
                    throw error;
                }
            },
            formData_fields() {
                let formData = new FormData();
                formData.append("id", this.formInputs.id);
                formData.append("firstname", this.formInputs.firstname);
                formData.append("lastname", this.formInputs.lastname);
                formData.append("email", this.formInputs.email);
                formData.append("phonenumber", this.formInputs.phonenumber);
                formData.append("website", this.formInputs.website);
                formData.append("latitude", this.$store.state.latitude);
                formData.append("longitude", this.$store.state.longitude);
                formData.append("sex", this.formInputs.sex);
                formData.append("status", this.formInputs.status);
                formData.append("tags", (this.formInputs.tags || []).join(","));
                formData.append("checkbox1", this.formInputs.checkbox1);
                formData.append("radio1", this.formInputs.radio1);
                formData.append("total_age", 22);
                formData.append("file", this.formInputs.file); // Make sure this is a valid file object
                formData.append("date", this.formInputs.date || "");   // "2026-10-29 14:30"
                formData.append("notes", this.formInputs.notes || "");
                formData.append("amount", this.formInputs.amount ?? "");
                formData.append("access_code", this.formInputs.access_code || ""); // empty = keep the current one
                formData.append("referred_by_id", this.formInputs.referred_by_id ?? "");
                formData.append("action", "saveOrEditRecord");
                return formData;
            },
            clearForm() {
            this.isSaving = false;
            this.isEditing = false;
            this.formInputs.id = "";
            this.formInputs.firstname = "";
            this.formInputs.lastname = "";
            this.formInputs.middlename = "";
            this.formInputs.email = "";
            this.formInputs.phonenumber = "";
            this.formInputs.website = "";
            this.formInputs.address = "";
            this.formInputs.sex = "";
            this.formInputs.status = "";
            this.formInputs.tags = [];
            this.formInputs.checkbox1 = false;
            this.formInputs.radio1 = "";
            this.formInputs.file = null; // empties the File Upload box (FormField)
            this.formInputs.date = "";
            this.formInputs.notes = "";
            this.formInputs.amount = null;
            this.formInputs.access_code = "";
            this.formInputs.access_code_confirm = "";
            this.formInputs.referred_by_id = null;
            this.formInputs.referred_by_name = "";
            this.$clearValidationStates("#recordForm");
        },
            editRecord(records) {
                this.isSaving = false;
                this.isEditing = true;
                this.formInputs.id = records.id ?? "";
                this.formInputs.firstname = records.firstname ?? "";
                this.formInputs.lastname = records.lastname ?? "";
                this.formInputs.email = records.email ?? "";
                this.formInputs.phonenumber = records.phonenumber ?? "";
                this.formInputs.website = records.website ?? "";
                this.formInputs.sex = records.sex ?? "";
                this.formInputs.status = records.status ?? "";
                this.formInputs.tags = (records.tags || []).map((tag) => tag.id);
                this.formInputs.checkbox1 = records.checkbox1 ?? "";
                this.formInputs.radio1 = records.radio1 ?? "";
                this.formInputs.date = records.date ?? ""; // DateField reads "2026-10-29 14:30:00" as it is
                this.formInputs.notes = records.notes ?? "";
                this.formInputs.amount = records.amount ?? null;
                this.formInputs.access_code = "";          // never shown; empty = keep the current one
                this.formInputs.access_code_confirm = "";
                this.formInputs.referred_by_id = records.referred_by_id ?? null;
                this.formInputs.referred_by_name = records.referred_by_name ?? "";

                // Don't re-upload a file chosen earlier (null also empties the box)
                this.formInputs.file = null;
                this.fetchDynamicFields(records.id);
                this.$clearValidationStates("#recordForm");
            },
            
            // fetchRecords, goToLastPage, deleteRecord, deleteSelectedRecords,
            // importRecords, downloadImportTemplate, exportRecords: in tableMixin (uses apiUrl)
        },
    };
</script>